/**
 * PORT-008 §5.3 — la barre de navigation d'un espace.
 *
 * Elle ne connaît qu'une chose : le **registre** `portalNav.ts`. Aucune garde
 * n'est écrite ici — pas un `if kind === …`, pas un `if capabilities.includes`.
 * C'est la raison d'être du registre : une garde par entrée, dispersée dans ce
 * fichier, redevient inauditable à la troisième facette.
 *
 * ⛔ Aucune mention d'Argalis : ni logo, ni nom, ni lien. La personne qui lit
 * cette barre est un client, un apprenant ou un intervenant de l'organisme.
 */

import { NavLink } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { LogOut } from 'lucide-react'

import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import type { PortalContext, PortalOrganisation } from '@/hooks/usePortalMe.types'

import { PORTAL_NAV_SOCLE, entreeEstMontee, type PortalNavItem } from './portalNav'

interface PortalSidebarProps {
    organisation: PortalOrganisation
    contexteActif: PortalContext | null
    /** Entrées de facette, ajoutées par les tickets d'écran. */
    entreesDeFacette?: PortalNavItem[]
    onNavigate?: () => void
    onDeconnexion: () => void
}

export function PortalSidebar({
    organisation,
    contexteActif,
    entreesDeFacette = [],
    onNavigate,
    onDeconnexion,
}: PortalSidebarProps) {
    const { t } = useTranslation()
    const entrees = [...PORTAL_NAV_SOCLE, ...entreesDeFacette].filter((e) =>
        entreeEstMontee(e, contexteActif),
    )

    return (
        <nav
            // DS §13.5 — le tiroir mobile passe `p-0`, donc les zones sûres se
            // posent ICI. Sans celle du HAUT, en PWA installée, le logo et le nom
            // de l'organisme passent SOUS la barre d'état : c'est précisément
            // l'identité que tout cet épic protège qui disparaîtrait.
            //
            // ⛔ Celle de GAUCHE n'est pas un doublon : ce panneau entre PAR la
            // gauche (`side="left"` ⇒ `inset-y-0 left-0`, `ui/sheet.tsx`), et
            // c'est le seul chemin de navigation d'un téléphone. En paysage sur
            // un appareil à encoche l'inset vaut 44-59 px contre 12 px de `p-3` :
            // l'icône et les premiers caractères de chaque libellé passent sous
            // le bloc capteurs. `max()` et non `calc()` — l'inset REMPLACE la
            // gouttière au lieu de s'y ajouter (AppLayout.tsx:155-158).
            //
            // ⚠️ Le corriger dans le PRIMITIF serait inerte : `sheetVariants` n'a
            // aucun padding de base, et les DEUX seuls appelants `side="left"` du
            // dépôt passent le leur (`p-0` ici, `p-4` dans `GedPageMobileSidebar`),
            // que tailwind-merge fait gagner. La garde vit chez l'appelant.
            className="flex h-full flex-col gap-1 p-3 pt-[calc(0.75rem+env(safe-area-inset-top))] pl-[max(0.75rem,env(safe-area-inset-left))] pb-[max(0.75rem,env(safe-area-inset-bottom))]"
            aria-label={t('portal.nav.label')}
        >
            <div className="flex items-center gap-2 px-2 py-3">
                {organisation.logo_url ? (
                    <img
                        src={organisation.logo_url}
                        alt={organisation.name}
                        // Agrandi à la demande du PO (2026-09-15) : à 32 px de haut et
                        // 9 rem de large, le logo d'un organisme était illisible. Il
                        // prend désormais la largeur de la barre, en gardant ses
                        // proportions (`object-contain`), calé à gauche.
                        className="h-16 w-auto max-w-full object-contain object-left"
                    />
                ) : (
                    <span className="truncate text-sm font-semibold">{organisation.name}</span>
                )}
            </div>
            <Separator className="mb-1" />

            {/* La liste défile, le reste non : ~150 px de chrome fixe (marque,
                séparateurs, déconnexion) ne laissent que 5 entrées en paysage, et
                les tickets de facette en versent davantage — sans défilement la
                déconnexion devient inatteignable.

                `min-h-0` est porteur : sans lui un enfant flex refuse de rétrécir
                sous la hauteur de son contenu, `overflow-y-auto` ne défile jamais,
                et le débordement pousse la déconnexion hors de l'écran — ce qu'on
                croyait empêcher. */}
            <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto">
                {entrees.map((entree) => (
                    <NavLink
                        key={`${entree.href}:${entree.label}`}
                        to={entree.href}
                        end={entree.href === '/espace/app'}
                        onClick={onNavigate}
                        className={({ isActive }) =>
                            cn(
                                // 44 px : plancher tactile, cette barre se lit à une
                                // main entre deux salles (§5.3, mobile d'abord).
                                'flex min-h-11 items-center gap-3 rounded-md px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                                isActive
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-accent hover:text-accent-foreground',
                            )
                        }
                    >
                        <entree.icon className="h-4 w-4 shrink-0" aria-hidden />
                        <span className="truncate">{t(entree.label)}</span>
                        {entree.badge?.() ? (
                            <span className="ml-auto rounded-full bg-primary/10 px-2 text-xs tabular-nums">
                                {entree.badge()}
                            </span>
                        ) : null}
                    </NavLink>
                ))}
            </div>

            <div className="shrink-0">
                <Separator className="my-2" />
                <button
                    type="button"
                    onClick={onDeconnexion}
                    className="flex min-h-11 w-full items-center gap-3 rounded-md px-3 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                    <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                    {t('portal.nav.logout')}
                </button>
            </div>
        </nav>
    )
}
